/**
 * Painel lateral de configuração do passo selecionado (issues #137 e #139).
 *
 * O formulário é montado a partir de `type.fields`, que vem do registry do
 * backend: tipo de nó novo com campos novos aparece aqui sem mudar o front.
 * Tipos de campo:
 *   text, textarea, number, time, select, multiselect — valores simples;
 *   weekdays    — dias da semana (1 = seg … 7 = dom);
 *   rules       — lista de regras {field, operator, value} da condição;
 *   cases       — caminhos do switch [{id, value}];
 *   webhook_url — URL pública do gatilho webhook (só leitura, com copiar) e o
 *                 último corpo recebido;
 *   kv_map      — linhas chave → valor (corpo JSON do "Chamar webhook").
 * Campos de texto de ações ganham um seletor que acrescenta {{grupo.campo}}.
 * Um select com `source` pede a lista ao editor (funis, etapas, membros,
 * WhatsApp); com `depends_on`, a lista depende de outro campo (etapas do funil).
 */

import { useState } from 'react'
import Icon from '../../components/icons.jsx'
import { isFieldHidden, metaFor } from './nodeMeta.js'

const DIAS = [
  { value: 1, label: 'Seg' }, { value: 2, label: 'Ter' }, { value: 3, label: 'Qua' },
  { value: 4, label: 'Qui' }, { value: 5, label: 'Sex' }, { value: 6, label: 'Sáb' },
  { value: 7, label: 'Dom' },
]

let seq = 0
const caseId = () => `c${Date.now().toString(36)}${(seq++).toString(36)}`

function optionsFor(field, config, sourceOptions) {
  if (!field.source) return field.options || []
  if (field.depends_on && !config[field.depends_on]) return []
  return sourceOptions(field.source, field.depends_on ? config[field.depends_on] : null) || []
}

function Weekdays({ value, onChange }) {
  const set = new Set((value || []).map(Number))
  const toggle = (d) => {
    const next = new Set(set)
    if (next.has(d)) next.delete(d); else next.add(d)
    onChange([...next].sort((a, b) => a - b))
  }
  return (
    <div className="aut-days" role="group">
      {DIAS.map((d) => (
        <button key={d.value} type="button" aria-pressed={set.has(d.value)}
          className={`aut-day${set.has(d.value) ? ' is-on' : ''}`} onClick={() => toggle(d.value)}>
          {d.label}
        </button>
      ))}
    </div>
  )
}

function MultiSelect({ options, value, onChange }) {
  const set = new Set(value || [])
  if (!options.length) return <p className="field-hint">Nenhuma opção disponível.</p>
  return (
    <div className="aut-multi">
      {options.map((o) => (
        <label key={o.value} className="aut-multi-item">
          <input type="checkbox" checked={set.has(o.value)}
            onChange={() => onChange(set.has(o.value)
              ? (value || []).filter((v) => v !== o.value)
              : [...(value || []), o.value])} />
          <span>{o.label}</span>
        </label>
      ))}
    </div>
  )
}

/**
 * Seletor de campo "grupo.campo" (#157). A opção "var." (variável do fluxo)
 * abre um campo de texto para o nome da variável: o valor vira "var.nome".
 */
function FieldPicker({ value, options, onChange, placeholder = 'Campo…' }) {
  const isVar = typeof value === 'string' && value.startsWith('var.')
  return (
    <>
      <select className="input" value={isVar ? 'var.' : (value || '')}
        onChange={(e) => onChange(e.target.value || null)}>
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {isVar && (
        <input className="input" placeholder="Nome da variável" value={value.slice(4)}
          onChange={(e) => onChange(`var.${e.target.value.replace(/[^A-Za-z0-9_]/g, '')}`)} />
      )}
    </>
  )
}

function Rules({ field, config, onChangeConfig, variables }) {
  // Condição antiga (#137): uma regra direto na config — vira lista ao editar.
  const rules = config.rules?.length ? config.rules
    : (config.field ? [{ field: config.field, operator: config.operator, value: config.value }] : [])
  const noValue = new Set(field.no_value_operators || [])
  const save = (next) => onChangeConfig({ ...config, rules: next, field: null, operator: null, value: null })
  const set = (i, patch) => save(rules.map((r, j) => (j === i ? { ...r, ...patch } : r)))

  return (
    <div className="aut-rules">
      {rules.map((r, i) => (
        <div key={i} className="aut-rule">
          <div className="aut-rule-head">
            <span className="t-label">Regra {i + 1}</span>
            <button type="button" className="aut-link-btn" onClick={() => save(rules.filter((_, j) => j !== i))}
              aria-label={`Remover regra ${i + 1}`}>
              <Icon n="x" s={13} />
            </button>
          </div>
          <FieldPicker value={r.field}
            options={[...(field.fields || []), ...(variables || []).filter((v) => v.value.startsWith('webhook.'))]}
            onChange={(v) => set(i, { field: v })} />
          <select className="input" value={r.operator || ''} onChange={(e) => set(i, { operator: e.target.value || null })}>
            <option value="">Operador…</option>
            {(field.operators || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {!noValue.has(r.operator) && (
            <input className="input" placeholder="Valor" value={r.value ?? ''}
              onChange={(e) => set(i, { value: e.target.value })} />
          )}
        </div>
      ))}
      <button type="button" className="btn btn-outline btn-xs"
        onClick={() => save([...rules, { field: null, operator: 'equals', value: '' }])}>
        <Icon n="plus" s={12} /> Adicionar regra
      </button>
    </div>
  )
}

function Cases({ value, onChange }) {
  const cases = value || []
  return (
    <div className="aut-cases">
      {cases.map((c, i) => (
        <div key={c.id} className="aut-case">
          <input className="input" placeholder={`Valor do caminho ${i + 1}`} value={c.value ?? ''}
            onChange={(e) => onChange(cases.map((x) => (x.id === c.id ? { ...x, value: e.target.value } : x)))} />
          <button type="button" className="btn-icon" aria-label={`Remover caminho ${i + 1}`}
            onClick={() => onChange(cases.filter((x) => x.id !== c.id))}>
            <Icon n="x" s={14} />
          </button>
        </div>
      ))}
      <button type="button" className="btn btn-outline btn-xs"
        onClick={() => onChange([...cases, { id: caseId(), value: '' }])}>
        <Icon n="plus" s={12} /> Adicionar caminho
      </button>
    </div>
  )
}

/** Select que acrescenta {{variável}} ao texto do campo. */
function VariablePicker({ variables, onPick }) {
  if (!variables?.length) return null
  return (
    <select className="input aut-var-pick" value="" aria-label="Inserir variável"
      onChange={(e) => { if (e.target.value) onPick(`{{${e.target.value}}}`) }}>
      <option value="">+ Inserir variável…</option>
      {variables.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
    </select>
  )
}

function KeyValueMap({ value, onChange, variables }) {
  const rows = value || []
  const set = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)))
  return (
    <div className="aut-cases">
      {rows.map((r, i) => (
        <div key={i} className="aut-kv">
          <input className="input" placeholder="Campo (ex.: email)" value={r.key ?? ''}
            onChange={(e) => set(i, { key: e.target.value })} />
          <input className="input" placeholder="Valor ou {{variável}}" value={r.value ?? ''}
            onChange={(e) => set(i, { value: e.target.value })} />
          <VariablePicker variables={variables} onPick={(v) => set(i, { value: v })} />
          <button type="button" className="btn-icon" aria-label={`Remover campo ${i + 1}`}
            onClick={() => onChange(rows.filter((_, j) => j !== i))}>
            <Icon n="x" s={14} />
          </button>
        </div>
      ))}
      <button type="button" className="btn btn-outline btn-xs"
        onClick={() => onChange([...rows, { key: '', value: '' }])}>
        <Icon n="plus" s={12} /> Adicionar campo
      </button>
    </div>
  )
}

function WebhookUrl({ url, sample, onRefresh }) {
  const [copiado, setCopiado] = useState(false)
  if (!url) return <p className="field-hint">Salve a automação para gerar a URL.</p>
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopiado(true)
      setTimeout(() => setCopiado(false), 1500)
    } catch { /* sem permissão de clipboard: o usuário copia na mão */ }
  }
  return (
    <div className="aut-webhook">
      <code>{url}</code>
      <button type="button" className="btn btn-outline btn-xs" onClick={copiar}>
        <Icon n={copiado ? 'check' : 'clip'} s={12} /> {copiado ? 'Copiado' : 'Copiar'}
      </button>
      <p className="field-hint">
        {sample
          ? 'Última chamada recebida (método, headers, query e body). Os campos aparecem em "Inserir variável" nas ações:'
          : 'Nenhuma chamada recebida ainda. Chame a URL (GET ou POST, com a automação inativa funciona) e clique em Atualizar.'}
      </p>
      {sample && <code>{JSON.stringify(sample, null, 2)}</code>}
      <button type="button" className="btn btn-outline btn-xs" onClick={onRefresh}>Atualizar campos recebidos</button>
    </div>
  )
}

function FieldInput({
  field, value, config, sourceOptions, onChange, onChangeConfig, webhookUrl,
  variables, webhookSample, onRefreshSample, isAction,
}) {
  const id = `aut-f-${field.name}`
  const append = (v) => onChange(`${value ?? ''}${v}`)
  switch (field.type) {
    case 'textarea':
      return (
        <>
          <textarea id={id} className="input aut-textarea" rows={4} value={value ?? ''}
            onChange={(e) => onChange(e.target.value)} />
          {isAction && <VariablePicker variables={variables} onPick={append} />}
        </>
      )
    case 'number':
      return <input id={id} className="input" type="number" min="0" value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} />
    case 'time':
      return <input id={id} className="input" type="time" value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)} />
    case 'weekdays':
      return <Weekdays value={value} onChange={onChange} />
    case 'multiselect':
      return <MultiSelect options={optionsFor(field, config, sourceOptions)} value={value} onChange={onChange} />
    case 'rules':
      return <Rules field={field} config={config} onChangeConfig={onChangeConfig} variables={variables} />
    case 'field_ref':
      return <FieldPicker value={value} options={field.options || []} onChange={onChange} />
    case 'cases':
      return <Cases value={value} onChange={onChange} />
    case 'webhook_url':
      return <WebhookUrl url={webhookUrl} sample={webhookSample} onRefresh={onRefreshSample} />
    case 'kv_map':
      return <KeyValueMap value={value} onChange={onChange} variables={variables} />
    case 'select': {
      const options = optionsFor(field, config, sourceOptions)
      const semPai = field.depends_on && !config[field.depends_on]
      const placeholder = semPai ? 'Escolha o funil primeiro' : field.required ? 'Selecione…' : 'Qualquer'
      return (
        <select id={id} className="input" value={value ?? ''} disabled={!!semPai}
          onChange={(e) => onChange(e.target.value || null)}>
          <option value="">{placeholder}</option>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )
    }
    default:
      return (
        <>
          <input id={id} className="input" type="text" value={value ?? ''}
            onChange={(e) => onChange(e.target.value)} />
          {isAction && <VariablePicker variables={variables} onPick={append} />}
        </>
      )
  }
}

export default function AutomacaoConfigPanel({
  node, type, sourceOptions, onChange, onDelete, onClose, webhookUrl,
  variables, webhookSample, onRefreshSample, testStep,
}) {
  if (!node) return null
  const config = node.data.config || {}
  const meta = metaFor(type)

  const setField = (field, value) => {
    const next = { ...config, [field.name]: value }
    // Trocar o funil invalida a etapa escolhida antes.
    if (field.name === 'pipeline_id') {
      type.fields.filter((f) => f.depends_on === 'pipeline_id').forEach((f) => { next[f.name] = null })
    }
    onChange(next)
  }

  return (
    <aside className="aut-panel" aria-label="Configuração do passo">
      <header className="aut-panel-head">
        <span className={`aut-node-icon tone-${meta.tone}`}><Icon n={meta.icon} s={16} /></span>
        <div className="aut-panel-titles">
          <span className="aut-node-kind">{meta.kindLabel}</span>
          <h3>{type?.label || node.data.nodeType}</h3>
        </div>
        <button type="button" className="btn-icon" onClick={onClose} aria-label="Fechar painel">
          <Icon n="x" s={16} />
        </button>
      </header>
      {type?.description && <p className="aut-panel-desc">{type.description}</p>}

      <div className="aut-panel-body">
        {(type?.fields || []).filter((f) => !isFieldHidden(f, config)).map((field) => (
          <div className="field" key={field.name}>
            <label className="field-label" htmlFor={`aut-f-${field.name}`}>
              {field.label}{field.required && <span className="aut-req"> *</span>}
            </label>
            <FieldInput
              field={field} value={config[field.name]} config={config}
              sourceOptions={sourceOptions} onChange={(v) => setField(field, v)}
              onChangeConfig={onChange} webhookUrl={webhookUrl}
              variables={variables} webhookSample={webhookSample}
              onRefreshSample={onRefreshSample} isAction={type?.kind === 'action'}
            />
            {field.help && <p className="field-hint">{field.help}</p>}
          </div>
        ))}
        {!type?.fields?.length && (
          <p className="aut-panel-empty">Este passo não precisa de configuração.</p>
        )}
      </div>

      {testStep && (
        <section className="aut-panel-test">
          <h4 className="t-label">Resultado do último teste</h4>
          {testStep.error && <p className="aut-error-text">{testStep.error}</p>}
          <pre>{JSON.stringify(testStep.output || {}, null, 2)}</pre>
        </section>
      )}

      <footer className="aut-panel-foot">
        <button type="button" className="btn btn-ghost aut-danger" onClick={onDelete}>
          <Icon n="trash" s={14} /> Remover passo
        </button>
      </footer>
    </aside>
  )
}
